import type { v2 } from '@deque/billing-service-client';
import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Icon,
  OptionsMenu,
  OptionsMenuItem,
  PageHeader,
  Panel,
  PanelContent,
  TextField
} from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState, type JSX } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import ContentToast from '~/common/components/ContentToast';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import { type ProductSlugs } from '~/common/constants';
import { type EnterpriseMembers } from '~/common/utils/billing-client/client-v2';
import type {
  FilterValues,
  FiltersLabelsAndValues
} from '../../hooks/useUserManagementFilters';
import type { AddUserFailedActionState } from '../AddUserProgress';
import BulkActionBar from './BulkActionBar/BulkActionBar';
import EditEmailModal from './EditEmailModal/EditEmailModal';
import FilterBar from './FilterBar/FilterBar';
import SearchBar from './SearchBar/SearchBar';
import styles from './UserManagement.css';
import UserManagementTable from './UserManagementTable/UserManagementTable';
import ViewAcceptedMemberDetails from './ViewDetails/ViewAcceptedMemberDetails/ViewAcceptedMemberDetails';
import ViewPendingMemberDetails from './ViewDetails/ViewPendingMemberDetails/ViewPendingMemberDetails';

export interface AvailableLicense {
  product_name?: string;
  product_slug?: string;
  remaining_count: number | null;
}

export interface UserManagementProps {
  filteredMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  filteredPendingUsers: EnterpriseMembers.PendingUser[];
  currentUserId: string;
  availableLicenses: AvailableLicense[];
  getProductBySlug: (slug: string) => v2.Product | undefined;
  onAddUser: (
    type: 'product' | 'admin' | 'member',
    productSlug?: string
  ) => void;
  onResend: (member: EnterpriseMembers.PendingMember) => void;
  onBulkResendInvitations: () => void;
  onRemoveUser: (member: EnterpriseMembers.PendingOrAcceptedMember) => void;
  onBulkRemoveUsers: () => void;
  onEditEmailSuccess: (newEmail: string) => void;
  enterpriseId: string;
  token: string;
  onDismiss: () => void;
  onExport: () => void;
  loadingAction: boolean;
  hasError: boolean;
  errMessage: string | null;
  confirmationMsg: string | null;
  isMobile?: boolean;
  hiddenProductSlugs: ProductSlugs[];
  isMailerEnabled: boolean;
  handleApplyFilters: (filters: FilterValues) => void;
  appliedFilters: FilterValues;
  isFiltered: boolean;
  allowedFilterValuesAndLabels: FiltersLabelsAndValues;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  selectedMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  setSelectedMembers: React.Dispatch<
    React.SetStateAction<EnterpriseMembers.PendingOrAcceptedMember[]>
  >;
  pendingSelectedMembers: EnterpriseMembers.PendingMember[];
  removeSelectedMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  selectedAcceptedMembers: EnterpriseMembers.AcceptedMember[];
}

const UserManagement = ({
  filteredMembers,
  currentUserId,
  availableLicenses,
  getProductBySlug,
  onAddUser,
  onResend,
  onBulkResendInvitations,
  onRemoveUser,
  onBulkRemoveUsers,
  onEditEmailSuccess,
  enterpriseId,
  token,
  onDismiss,
  onExport,
  hasError,
  errMessage,
  loadingAction,
  confirmationMsg,
  isMobile = false,
  hiddenProductSlugs,
  isMailerEnabled,
  handleApplyFilters,
  appliedFilters,
  isFiltered,
  allowedFilterValuesAndLabels,
  searchQuery,
  setSearchQuery,
  selectedMembers,
  setSelectedMembers,
  pendingSelectedMembers,
  removeSelectedMembers,
  selectedAcceptedMembers
}: UserManagementProps): JSX.Element => {
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [showResendAlert, setShowResendAlert] = useState(false);
  const [showBulkResendAlert, setShowBulkResendAlert] = useState(false);
  const [showBulkRemoveAlert, setShowBulkRemoveAlert] = useState(false);
  const [showBulkRemoveAlertError, setShowBulkRemoveAlertError] =
    useState(false);
  const [showBulkEditAccessAlertError, setShowBulkEditAccessAlertError] =
    useState(false);
  const [user, setUser] =
    useState<EnterpriseMembers.PendingOrAcceptedMember | null>(null);
  const [showAcceptedMemberDetails, setShowAcceptedMemberDetails] =
    useState<EnterpriseMembers.AcceptedMember | null>(null);
  const [showPendingMemberDetails, setShowPendingMemberDetails] =
    useState<EnterpriseMembers.PendingMember | null>(null);
  const [editingEmailMember, setEditingEmailMember] =
    useState<EnterpriseMembers.AcceptedMember | null>(null);
  const location = useLocation();
  // TODO: cast pending router/history typings cleanup — see
  // https://github.com/dequelabs/walnut/issues/14586
  const failedUserAction = location.state as AddUserFailedActionState;
  const [showActionFailedAlert, setShowActionFailedAlert] = useState(
    !!failedUserAction?.action && failedUserAction?.failedUsers?.length
  );
  const dismissActionFailedAlert = () => setShowActionFailedAlert(false);

  // Close the member details drawer when launching a confirmation alert: the
  // drawer would otherwise overlay the alert (mobile/narrow viewports) or be
  // overlaid by it (wider viewports), leaving the alert inaccessible.
  const closeMemberDetailsDrawer = () => {
    setShowAcceptedMemberDetails(null);
    setShowPendingMemberDetails(null);
  };

  const onResendClick = (member: EnterpriseMembers.PendingMember) => {
    setShowResendAlert(true);
    setUser(member);
    closeMemberDetailsDrawer();
  };

  const onRemoveClick = (member: EnterpriseMembers.PendingOrAcceptedMember) => {
    setShowRemoveAlert(true);
    setUser(member);
    closeMemberDetailsDrawer();
  };

  const onEditEmailClick = (member: EnterpriseMembers.AcceptedMember) => {
    setEditingEmailMember(member);
    setShowAcceptedMemberDetails(null);
  };

  const handleEditEmailSuccess = (newEmail: string) => {
    setEditingEmailMember(null);
    onEditEmailSuccess(newEmail);
  };

  const onBulkResendClick = () => {
    if (pendingSelectedMembers.length > 100) {
      setShowBulkRemoveAlertError(true);
    } else {
      setShowBulkResendAlert(true);
    }
  };

  const onBulkRemoveClick = () => {
    setShowBulkRemoveAlert(true);
  };

  const onBulkEditAccessClick = () => {
    if (selectedAcceptedMembers.length > 100) {
      setShowBulkEditAccessAlertError(true);
    }
  };

  useEffect(() => {
    /* istanbul ignore next */
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  useEffect(() => {
    if (!statusRef.current) {
      return;
    }

    if (filteredMembers.length) {
      statusRef.current.textContent = null;
      return;
    }

    const textContent =
      isFiltered || !!searchQuery
        ? t(
            'No results found. Try adjusting your filters or searching for someone else.'
          )
        : t('Please add your first user!');

    const timeoutId = setTimeout(() => {
      if (statusRef.current) {
        statusRef.current.textContent = textContent;
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [statusRef.current, isFiltered, searchQuery, filteredMembers.length]);

  return (
    <div
      className={classNames(styles.wrap, {
        [styles.collapsed]: isMobile
      })}
    >
      {loadingAction && (
        <ScrimmedLoader
          label={t('Loading...')}
          loaderRef={loaderRef}
          focusTrap
        />
      )}
      <Alert heading={t('resend invitation')} show={showResendAlert}>
        <AlertContent>
          {t('You’re about to send another invitation to: {{ email }}.', {
            email: user?.email
          })}
        </AlertContent>
        <AlertActions>
          <Button
            onClick={() => {
              onResend(user as EnterpriseMembers.PendingMember);
              setShowResendAlert(false);
            }}
          >
            {t('Resend')}
          </Button>
          <Button variant="secondary" onClick={() => setShowResendAlert(false)}>
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>
      <Alert heading={t('Resend Invitation')} show={showBulkResendAlert}>
        <AlertContent>
          {pendingSelectedMembers.length === 1
            ? t(
                'You are about to send another invitation to {{ userCount }} user.',
                {
                  userCount: pendingSelectedMembers.length
                }
              )
            : t(
                'You are about to send another invitation to {{ userCount }} users.',
                {
                  userCount: pendingSelectedMembers.length
                }
              )}
        </AlertContent>
        <AlertActions>
          <Button
            onClick={() => {
              onBulkResendInvitations();
              setShowBulkResendAlert(false);
            }}
          >
            {t('Resend')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowBulkResendAlert(false)}
          >
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>
      <Alert
        heading={t('Selection Limit Exceeded')}
        show={showBulkEditAccessAlertError}
      >
        <AlertContent>
          {t(
            'You have selected more than 100 users. Please reduce your selection to 100 or fewer users to edit access.'
          )}
        </AlertContent>
        <AlertActions>
          <Button
            variant="secondary"
            onClick={() => setShowBulkEditAccessAlertError(false)}
          >
            {t('Close')}
          </Button>
        </AlertActions>
      </Alert>
      <Alert
        heading={t('Selection Limit Exceeded')}
        show={showBulkRemoveAlertError}
      >
        <AlertContent>
          {t(
            'You have selected more than 100 users. Please reduce your selection to 100 or fewer users to re-send invites.'
          )}
        </AlertContent>
        <AlertActions>
          <Button
            variant="secondary"
            onClick={() => setShowBulkRemoveAlertError(false)}
          >
            {t('Close')}
          </Button>
        </AlertActions>
      </Alert>
      <Alert
        heading={t('Remove User')}
        show={showRemoveAlert}
        variant="warning"
      >
        <AlertContent>
          {t(
            'You’re about to revoke user access for: {{ email }}. They will no longer be part of your managed users.',
            { email: user?.email }
          )}
        </AlertContent>
        <AlertActions>
          <Button
            variant="error"
            onClick={() => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              onRemoveUser(user!);
              setShowRemoveAlert(false);
            }}
          >
            {t('Remove')}
          </Button>
          <Button variant="secondary" onClick={() => setShowRemoveAlert(false)}>
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>
      <Alert
        heading={t('Remove User')}
        show={showBulkRemoveAlert}
        variant="warning"
      >
        <AlertContent>
          {removeSelectedMembers.length === 1
            ? t(
                'You are about to revoke access for the 1 selected user. They will no longer be part of your managed users.'
              )
            : t(
                'You are about to revoke access for the {{ selectedCount }} selected users. They will no longer be part of your managed users.',
                { selectedCount: removeSelectedMembers.length }
              )}
        </AlertContent>
        <AlertActions>
          <Button
            variant="error"
            onClick={() => {
              onBulkRemoveUsers();
              setShowBulkRemoveAlert(false);
            }}
          >
            {t('Remove')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowBulkRemoveAlert(false)}
          >
            {t('Cancel')}
          </Button>
        </AlertActions>
      </Alert>
      {hasError && (
        <ContentToast type="caution" show>
          {t('Failed to load users')}
        </ContentToast>
      )}
      {errMessage && (
        <ContentToast type="caution" show>
          {errMessage}
        </ContentToast>
      )}
      {confirmationMsg && (
        <ContentToast type="confirmation" show onDismiss={onDismiss}>
          {confirmationMsg}
        </ContentToast>
      )}
      {showAcceptedMemberDetails && (
        <ViewAcceptedMemberDetails
          member={showAcceptedMemberDetails}
          onViewDetailsClose={closeMemberDetailsDrawer}
          onRemoveClick={onRemoveClick}
          onEditEmailClick={onEditEmailClick}
        />
      )}
      {showPendingMemberDetails && (
        <ViewPendingMemberDetails
          member={showPendingMemberDetails}
          onViewDetailsClose={closeMemberDetailsDrawer}
          onResendInvitation={onResendClick}
          onRemoveClick={onRemoveClick}
        />
      )}
      {editingEmailMember && (
        <EditEmailModal
          member={editingEmailMember}
          enterpriseId={enterpriseId}
          token={token}
          onClose={() => setEditingEmailMember(null)}
          onSuccess={handleEditEmailSuccess}
        />
      )}
      {!hasError && (
        <div>
          <div className={styles.head}>
            <div className={styles.header}>
              <PageHeader heading={t('Users')} />
            </div>
            <div className={styles.buttonGroup}>
              <OptionsMenu
                className={styles.addUsersMenu}
                align={isMobile ? 'left' : 'right'}
                trigger={triggerProps => (
                  <Button type="button" {...triggerProps} thin>
                    <Icon type="plus" />
                    {t('Add users')}
                  </Button>
                )}
              >
                {availableLicenses.map(license => (
                  <OptionsMenuItem
                    key={license.product_slug}
                    disabled={
                      license.remaining_count !== null &&
                      license.remaining_count <= 0
                    }
                    onSelect={() => onAddUser('product', license.product_slug)}
                  >
                    <p>
                      {t('...to {{ productName }}', {
                        productName: license.product_name
                      })}
                      {license.remaining_count !== null && (
                        <span>
                          {t(' ({{ licenseCount }} users available)', {
                            licenseCount: license.remaining_count
                          })}
                        </span>
                      )}
                    </p>
                  </OptionsMenuItem>
                ))}
                <OptionsMenuItem onSelect={() => onAddUser('member')}>
                  <p>{t('...to your Axe Account')}</p>
                </OptionsMenuItem>
                <OptionsMenuItem onSelect={() => onAddUser('admin')}>
                  <p>{t('...as an Axe Account Admin')}</p>
                </OptionsMenuItem>
              </OptionsMenu>
              <Button variant="secondary" thin onClick={onExport}>
                {t('Export to CSV')} <Icon type="export-solid" />
              </Button>
            </div>
          </div>
          <Panel>
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isFiltered={isFiltered}
              totalMembers={filteredMembers.length}
            />

            <FilterBar
              appliedFilters={appliedFilters}
              handleApplyFilters={handleApplyFilters}
              allowedFilterValuesAndLabels={allowedFilterValuesAndLabels}
            />
            {selectedMembers.length > 0 && (
              <BulkActionBar
                selectedMembers={selectedMembers}
                onResendClick={onBulkResendClick}
                pendingSelectedMembers={pendingSelectedMembers}
                removeSelectedMembers={removeSelectedMembers}
                selectedAcceptedMembers={selectedAcceptedMembers}
                onBulkRemoveClick={onBulkRemoveClick}
                onBulkEditAccessClick={onBulkEditAccessClick}
                isMobile={isMobile}
              />
            )}
            <PanelContent className={styles.tableContainer}>
              <p
                role="status"
                ref={statusRef}
                className={
                  filteredMembers.length ? styles.hiddenStatus : undefined
                }
              />
              {!!filteredMembers.length && (
                <UserManagementTable
                  filteredMembers={filteredMembers}
                  currentUserId={currentUserId}
                  getProductBySlug={getProductBySlug}
                  onResendClick={onResendClick}
                  onRemoveClick={onRemoveClick}
                  onEditEmailClick={onEditEmailClick}
                  isMobile={isMobile}
                  hiddenProductSlugs={hiddenProductSlugs}
                  isMailerEnabled={isMailerEnabled}
                  searchQuery={searchQuery}
                  selectedMembers={selectedMembers}
                  setSelectedMembers={setSelectedMembers}
                  setShowAcceptedMemberDetails={setShowAcceptedMemberDetails}
                  setShowPendingMemberDetails={setShowPendingMemberDetails}
                />
              )}
            </PanelContent>
          </Panel>
        </div>
      )}
      {showActionFailedAlert && failedUserAction && (
        <Alert
          heading={
            failedUserAction.action === 'add-users'
              ? t('Add User Complete')
              : t('Action Complete')
          }
          show
        >
          <AlertContent>
            {failedUserAction.failedReasons.length > 0 && (
              <ul>
                {failedUserAction.failedReasons.map(
                  (reason: string, index: number) => (
                    <li key={index}>{reason}</li>
                  )
                )}
              </ul>
            )}
            <p>
              {failedUserAction.failedUsers.length === 1
                ? t('We were unable to add (1) user:')
                : t('We were unable to add ({{ count }}) users:', {
                    count: failedUserAction.failedUsers.length
                  })}
            </p>
            <TextField
              label={t('Users not added')}
              value={failedUserAction.failedUsers.join(', ')}
              disabled
              multiline
            />
            <div className={styles.copyUsers}>
              <CopyToClipboard text={failedUserAction.failedUsers.join(', ')}>
                <Button variant="link">{t('copy list of users')}</Button>
              </CopyToClipboard>
            </div>
          </AlertContent>
          <AlertActions>
            <Button variant="secondary" onClick={dismissActionFailedAlert}>
              {t('Close')}
            </Button>
          </AlertActions>
        </Alert>
      )}
    </div>
  );
};
export default UserManagement;
